import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Button, CircularProgress, Divider, FormControlLabel, Grid, IconButton, MenuItem, Switch, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, tableCellClasses,
} from '@mui/material';
import styled from '@emotion/styled';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import CardHeaderBar from '../CardHeaderBar/CardHeaderBar';
import EditButton from '../EditButton/EditButton';
import ConfirmDialog from '../Dialog/ConfirmDialog';
import axios from '../../utils/axios';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  deleteCustomTemplate, fetchCustomTemplateDefaults, fetchCustomTemplates, saveCustomTemplate,
} from '../../store/invoiceStore/invoiceThunk';
import { ITemplate, ITemplateColumn, ITemplateConfig, ITemplateOption } from '../../utils/invoice.types';
import { PermissionsEnum } from '../../utils/auth.types';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#fff',
    color: '#9da7b7',
    borderTop: '0.25px solid #d9d4d4',
    borderBottom: '0.25px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: 13,
    paddingLeft: '15px !important',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: 'none',
    paddingLeft: '15px !important',
    margin: 0,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(even)': { backgroundColor: '#fbfbfb' },
  '&:nth-of-type(odd)': { backgroundColor: '#e9ecf1' },
}));

// Display names of the options the backend sends (keys must match utils/templateConfig.js).
const OPTION_NAMES: Record<string, string> = {
  title: 'Document title',
  subTitle: 'Sub title',
  logo: 'Logo',
  companyName: 'Business name',
  companyAddress: 'Business address',
  website: 'Website',
  email: 'Email',
  phone: 'Phone',
  gstin: 'GSTIN',
  docDate: 'Document date',
  docNumber: 'Document number',
  dueDate: 'Due / expiry date',
  billTo: 'Bill to',
  shipTo: 'Ship to',
  placeOfSupply: 'Place of supply',
  references: 'Reference fields (PO, quotation, additional fields)',
  description: 'Item description (under item name)',
  bankDetails: 'Bank details',
  qrCode: 'UPI QR code',
  terms: 'Terms and conditions',
  notes: 'Note / disclaimer',
  summary: 'Summary (totals)',
  amountInWords: 'Amount in words',
  receiverSignature: 'Receiver signature',
  authorisedSignature: 'Authorised signature',
};

const COLUMN_NAMES: Record<string, string> = {
  sno: 'Serial number',
  item: 'Item',
  hsn: 'HSN / SAC',
  mrp: 'MRP',
  discount: 'Discount',
  rate: 'Rate',
  qty: 'Quantity',
  amount: 'Amount',
  tax: 'Tax',
  total: 'Total',
};

const MAX_LABEL_LENGTH = 60;
const hintStyle = { fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 12 } as const;
// Designer panes fill the screen below the app header and the settings tabs.
const PANE_HEIGHT = 'calc(100vh - 215px)';

interface IOptionRowProps {
  name: string;
  option: ITemplateOption;
  onChange: (patch: Partial<ITemplateOption>) => void;
  /** Drag-to-reorder (line item columns): the handle starts the drag, the row is the drop target. */
  drag?: { active: boolean; dragging: boolean; onStart: () => void; onEnd: () => void; onDrop: () => void };
}

// One option: show/hide switch + (when the option prints a label) the label text.
const OptionRow: FC<IOptionRowProps> = ({ name, option, onChange, drag }) => (
  <div
    onDragOver={(e) => { if (drag?.active) e.preventDefault(); }}
    onDrop={() => drag?.onDrop()}
    style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '2px 8px', marginBottom: 6, borderRadius: 8,
      background: drag?.dragging ? '#eef2f7' : '#f7f8fa', opacity: drag?.dragging ? 0.5 : 1,
    }}
  >
    {drag ? (
      <span draggable onDragStart={drag.onStart} onDragEnd={drag.onEnd} style={{ display: 'flex', cursor: 'grab' }}>
        <DragIndicatorIcon fontSize="small" style={{ color: '#b0b8c4' }} />
      </span>
    ) : null}
    <FormControlLabel
      sx={{ flex: 1, mr: 0 }}
      control={<Switch checked={!!option.show} onChange={(e) => onChange({ show: e.target.checked })} size="small" />}
      label={<span style={{ fontSize: 14, color: '#000' }}>{name}</span>}
    />
    {option.label !== undefined ? (
      <TextField size="small" variant="standard" placeholder="Label" value={option.label} disabled={!option.show}
        onChange={(e) => onChange({ label: e.target.value })} sx={{ width: 190 }} inputProps={{ maxLength: MAX_LABEL_LENGTH }} />
    ) : null}
  </div>
);

/**
 * Voucher templates: the user builds named PDF templates per voucher (header, line item columns, footer);
 * each one then shows in that voucher's template dropdown and in Document Settings.
 */
const TemplateSettings: FC = () => {
  const dispatch = useAppDispatch();
  const { permissions } = useAppSelector((state) => state.auth);
  const canUpdateAccount = permissions?.includes(PermissionsEnum.Account_Update);

  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [voucherTypes, setVoucherTypes] = useState<string[]>([]);
  const [templateToDelete, setTemplateToDelete] = useState<ITemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Designer state; `editing` false = list view.
  const [editing, setEditing] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [name, setName] = useState('');
  const [voucherType, setVoucherType] = useState('');
  const [config, setConfig] = useState<ITemplateConfig | null>(null);
  const [saving, setSaving] = useState(false);

  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const previewRequest = useRef(0);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    const res: any = await dispatch(fetchCustomTemplates());
    if (!res.error) setTemplates(res.payload || []);
    setLoading(false);
  }, [dispatch]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // Loads the default options of a voucher type; returns false when the request fails.
  const loadDefaults = async (type?: string) => {
    const res: any = await dispatch(fetchCustomTemplateDefaults(type));
    if (res.error) {
      toast.error('Could not load the template options.');
      return false;
    }
    setVoucherTypes(res.payload.voucherTypes);
    setVoucherType(res.payload.voucherType);
    setConfig(res.payload.config);
    return true;
  };

  const openNew = async () => {
    if (!(await loadDefaults())) return;
    setTemplateId('');
    setName('');
    setEditing(true);
  };

  const openEdit = (template: ITemplate) => {
    if (!template.config || !template.voucherType) return;
    setTemplateId(template._id);
    setName(template.template);
    setVoucherTypes([template.voucherType]);
    setVoucherType(template.voucherType);
    setConfig(template.config);
    setEditing(true);
  };

  const closeEditor = () => {
    setEditing(false);
    setConfig(null);
    setPreviewError('');
  };

  // Sample PDF for the current options; only the latest request is shown.
  const loadPreview = useCallback(async (type: string, cfg: ITemplateConfig) => {
    const requestNo = ++previewRequest.current;
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const response = await axios.post('/api/v1/invoice/custom-templates/preview', { voucherType: type, config: cfg }, { responseType: 'blob' });
      if (requestNo !== previewRequest.current) return;
      setPreviewUrl(window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })));
    } catch (error: any) {
      if (requestNo !== previewRequest.current) return;
      setPreviewError('Preview could not be generated.');
    } finally {
      if (requestNo === previewRequest.current) setPreviewLoading(false);
    }
  }, []);

  // Refresh the preview shortly after the user stops changing options.
  useEffect(() => {
    if (!editing || !config || !voucherType) return;
    const timer = setTimeout(() => loadPreview(voucherType, config), 700);
    return () => clearTimeout(timer);
  }, [editing, config, voucherType, loadPreview]);

  useEffect(() => () => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const setOption = (section: 'header' | 'footer', key: string, patch: Partial<ITemplateOption>) =>
    setConfig((prev) => (prev ? { ...prev, [section]: { ...prev[section], [key]: { ...prev[section][key], ...patch } } } : prev));

  const setColumns = (columns: ITemplateColumn[]) =>
    setConfig((prev) => (prev ? { ...prev, lineItems: { ...prev.lineItems, columns } } : prev));

  const setColumn = (index: number, patch: Partial<ITemplateOption>) => {
    if (config) setColumns(config.lineItems.columns.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  // Drag-and-drop reordering (native HTML5 DnD, same as Barcode Format).
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const reorderColumn = (from: number, to: number) => {
    if (!config || from === to) return;
    const next = [...config.lineItems.columns];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setColumns(next);
  };

  const onSave = async () => {
    if (!config) return;
    if (!name.trim()) {
      toast.error('Template name is required.');
      return;
    }
    setSaving(true);
    const res: any = await dispatch(saveCustomTemplate({ id: templateId || undefined, name: name.trim(), voucherType, config }));
    setSaving(false);
    if (res.error) {
      toast.error(res.payload?.message || 'Could not save the template.');
      return;
    }
    toast.success(templateId ? 'Template updated.' : 'Template created.');
    closeEditor();
    loadTemplates();
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;
    setDeleting(true);
    const res: any = await dispatch(deleteCustomTemplate(templateToDelete._id));
    setDeleting(false);
    if (res.error) {
      toast.error(res.payload?.message || 'Could not delete the template.');
      return;
    }
    toast.success('Template deleted.');
    setTemplateToDelete(null);
    loadTemplates();
  };

  if (editing && config) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <div style={{ height: PANE_HEIGHT, overflowY: 'auto', paddingRight: 6 }}>
          <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, marginBottom: 16 }}>
            <CardHeaderBar
              title={templateId ? 'Edit Template' : 'New Template'}
              action={
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="outlined" color="inherit" size="small" onClick={closeEditor} disabled={saving}
                    sx={{ color: '#7a8290', borderColor: '#cdd3db', textTransform: 'none' }}>
                    Cancel
                  </Button>
                  <Button variant="contained" size="small" onClick={onSave} disabled={saving}
                    sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, textTransform: 'none' }}>
                    {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
                  </Button>
                </div>
              }
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={7}>
                <TextField fullWidth size="small" label="Template name" value={name} required
                  onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: MAX_LABEL_LENGTH }} />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField select fullWidth size="small" label="Voucher" value={voucherType} disabled={!!templateId}
                  onChange={(e) => loadDefaults(e.target.value)}>
                  {voucherTypes.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            {!templateId ? (
              <h5 style={{ ...hintStyle, marginTop: 10, marginBottom: 0 }}>Changing the voucher resets the options below to that voucher's defaults.</h5>
            ) : null}
          </div>

          <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, marginBottom: 16 }}>
            <CardHeaderBar title='Header Section' />
            {Object.keys(config.header).map((key) => (
              <OptionRow key={key} name={OPTION_NAMES[key] || key} option={config.header[key]} onChange={(patch) => setOption('header', key, patch)} />
            ))}
          </div>

          <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, marginBottom: 16 }}>
            <CardHeaderBar title='Line Items Table' />
            <h5 style={hintStyle}>Columns print left to right in this order (top → bottom = left → right); drag a row to reorder. MRP and Discount print only when an item on the voucher has a value.</h5>
            {config.lineItems.columns.map((column, i) => (
              <OptionRow
                key={column.key}
                name={COLUMN_NAMES[column.key] || column.key}
                option={column}
                onChange={(patch) => setColumn(i, patch)}
                drag={{
                  active: dragIndex !== null,
                  dragging: dragIndex === i,
                  onStart: () => setDragIndex(i),
                  onEnd: () => setDragIndex(null),
                  onDrop: () => { if (dragIndex !== null) reorderColumn(dragIndex, i); setDragIndex(null); },
                }}
              />
            ))}
            <Divider sx={{ my: 1 }} />
            <OptionRow name={OPTION_NAMES.description} option={config.lineItems.description}
              onChange={(patch) => setConfig((prev) => (prev ? { ...prev, lineItems: { ...prev.lineItems, description: { ...prev.lineItems.description, ...patch } } } : prev))} />
          </div>

          <div className={'c-card2'} style={{ padding: 15, paddingTop: 10 }}>
            <CardHeaderBar title='Footer Section' />
            <h5 style={hintStyle}>
              Bank details print when a bank account is chosen on the voucher. The UPI QR code prints when a UPI ID is set for the business, and never on quotations.
            </h5>
            {Object.keys(config.footer).map((key) => (
              <OptionRow key={key} name={OPTION_NAMES[key] || key} option={config.footer[key]} onChange={(patch) => setOption('footer', key, patch)} />
            ))}
          </div>
          </div>
        </Grid>

        <Grid item xs={12} md={7}>
          <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, height: PANE_HEIGHT, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
            <CardHeaderBar
              title='Preview'
              loading={previewLoading}
              action={<EditButton size='small' label='Refresh' icon={<RefreshIcon />} onClick={() => loadPreview(voucherType, config)} disabled={previewLoading} />}
            />
            <h5 style={hintStyle}>Sample voucher with your business details. Saved vouchers pick up template changes when their PDF is regenerated.</h5>
            {previewError ? (
              <div style={{ fontSize: 13, color: '#c0392b', marginBottom: 10 }}>{previewError}</div>
            ) : null}
            {previewUrl ? (
              <iframe title="Template preview" src={`${previewUrl}#toolbar=0&navpanes=0&view=FitH`} style={{ width: '100%', flex: 1, minHeight: 0, border: '1px solid #dcdcdc', borderRadius: 6 }} />
            ) : (
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9da7b7', fontSize: 13 }}>
                {previewLoading ? 'Generating preview…' : 'No preview yet'}
              </div>
            )}
          </div>
        </Grid>
      </Grid>
    );
  }

  return (
    <div className={'c-card2'} style={{ padding: 0 }}>
      <CardHeaderBar
        title='Voucher Templates'
        cardPaddingX={0}
        cardPaddingTop={0}
        loading={loading}
        action={canUpdateAccount ? <EditButton size='small' label='Add Template' icon={<AddOutlinedIcon />} onClick={openNew} /> : undefined}
      />
      <h5 style={{ ...hintStyle, padding: '0 15px' }}>
        Build your own PDF template for a voucher. A saved template appears in that voucher's template dropdown and under Default PDF Template in Document Settings.
      </h5>

      {templates.length > 0 ?
        <div style={{ marginBottom: 12, marginTop: 5 }}>
          <Table stickyHeader sx={{ width: '100%' }} size='small' aria-label="voucher templates table">
            <TableHead>
              <TableRow>
                <StyledTableCell width={300}>Template Name</StyledTableCell>
                <StyledTableCell width={250}>Voucher</StyledTableCell>
                <StyledTableCell width={100}>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((t) =>
                <StyledTableRow key={t._id}>
                  <StyledTableCell>{t.template}</StyledTableCell>
                  <StyledTableCell>{t.voucherType}</StyledTableCell>
                  <StyledTableCell align='left'>
                    {canUpdateAccount &&
                      <IconButton
                        onClick={() => openEdit(t)}
                        style={{ color: '#488dff', backgroundColor: '#fff', height: 25, width: 25 }}
                        size='small' ><EditIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    }
                    {canUpdateAccount &&
                      <IconButton
                        onClick={() => setTemplateToDelete(t)}
                        style={{ color: '#c33b3b', backgroundColor: '#fff', height: 25, width: 25, marginLeft: 10 }}
                        size='small' ><DeleteIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    }
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </div> :
        <div style={{ height: 200, padding: '0 15px' }}>
          <Divider sx={{ mt: 0.5, mb: 1 }} />
          {loading ? '' : 'No templates yet'}</div>
      }

      <ConfirmDialog
        open={!!templateToDelete}
        title="Delete Template"
        message={<>Are you sure you want to delete the template <b>{templateToDelete?.template}</b>?</>}
        warningTitle="Before you delete"
        warningText="A template that is used by a voucher, or set as a default in Document Settings, cannot be deleted."
        confirmLabel="Delete Template"
        loading={deleting}
        onConfirm={confirmDelete}
        onClose={() => setTemplateToDelete(null)}
      />
    </div>
  );
};

export default TemplateSettings;
